import { memo, useCallback, useMemo } from 'react';
import { cn } from '@divlab/divanui';
import { IconChevronRight, IconDelete, IconInfo } from '@divlab/divanui/icons';

import useNavigation from '@Navigation/useNavigation';
import * as PickPairStore from '@Stores/PickPair';
import useSearchParams from '@Hooks/useSearchParams';
import Img from '@UI/Img';
import styles from './Option.module.css';

import type { FC, HTMLAttributes, MouseEvent } from 'react';
import type { PickPairData } from '@Api/Product/getSetItem';

export interface OptionProp extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  product: PickPairData;
  canBeDeleted?: boolean;
  error?: boolean;
  onClickOption?: (e: MouseEvent, id: number) => void;
}

const Option: FC<OptionProp> = (props) => {
  const { product, canBeDeleted, error, onClickOption, className, ...restProps } = props;
  const { type, fabrics, id, warning, parameters, productParameters } = product;
  const searchParams = useSearchParams();
  const { navigate } = useNavigation();

  const fabric = useMemo(() => {
    return fabrics.items?.find((item) => item.selected);
  }, [fabrics]);
  const params = useMemo(() => {
    const allParams = productParameters || parameters;
    return allParams.reduce((prev, current) => {
      const val = current.items
        .flat()
        .map((item) => item.value)
        .join('x');
      return `${prev}${prev.length > 0 ? ', ' : ''}${val}`;
    }, fabric?.name);
  }, [parameters, fabric, productParameters]);

  const handleDeleteOption = useCallback(async () => {
    PickPairStore.remove(product);
    const url = await PickPairStore.getConstructorUrl({ searchParams });

    navigate(url, { replace: true });
  }, [searchParams, product, navigate]);

  const handleClickOption = useCallback(
    (e: MouseEvent) => {
      if (onClickOption) onClickOption(e, id);
    },
    [id, onClickOption],
  );

  return (
    <div
      {...restProps}
      className={cn(styles.option, { [styles.canBeDeleted]: canBeDeleted }, className)}
    >
      <div className={styles.wrapper}>
        <div className={styles.content} onClick={handleClickOption}>
          <Img src={fabric?.image} className={styles.image} cnImage={styles.cnImage} />

          <div className={styles.textContent}>
            <div className={styles.name}>{type.name}</div>
            <div className={styles.characteristics}>{params}</div>
          </div>
          <IconChevronRight className={styles.iconArrow} />
        </div>
        {canBeDeleted && (
          <div className={styles.iconDelete} onClick={handleDeleteOption} data-testid='trash'>
            <IconDelete />
          </div>
        )}
      </div>

      {warning && (
        <div className={cn(styles.notice, { [styles.active]: error })}>
          <IconInfo className={styles.iconInfo} filled={false} />
          <span className={styles.noticeText}>{warning}</span>
        </div>
      )}
    </div>
  );
};

export default memo(Option);
